import { useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate or retrieve visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem('visitor_id', visitorId);
  }
  return visitorId;
};

// Get device type
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'Tablet';
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) return 'Mobile';
  return 'Desktop';
};

// Get browser name
const getBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('SamsungBrowser')) return 'Samsung Browser';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  if (ua.includes('Trident')) return 'IE';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Unknown';
};

// Get traffic source
const getSource = () => {
  const ref = document.referrer;
  if (!ref) return 'Direct';
  
  try {
    const refUrl = new URL(ref);
    const host = refUrl.hostname;
    
    if (host.includes('google')) return 'Google';
    if (host.includes('bing')) return 'Bing';
    if (host.includes('yahoo')) return 'Yahoo';
    if (host.includes('facebook') || host.includes('fb.')) return 'Facebook';
    if (host.includes('twitter') || host.includes('t.co')) return 'Twitter';
    if (host.includes('linkedin')) return 'LinkedIn';
    if (host.includes('instagram')) return 'Instagram';
    if (host.includes('youtube')) return 'YouTube';
    if (host.includes('reddit')) return 'Reddit';
    
    return refUrl.hostname;
  } catch {
    return 'Direct';
  }
};

// Get approximate location from timezone
const getApproxLocation = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzMap = {
    'Europe/Helsinki': { country: 'Finland', city: 'Helsinki' },
    'Europe/Stockholm': { country: 'Sweden', city: 'Stockholm' },
    'Europe/Oslo': { country: 'Norway', city: 'Oslo' },
    'Europe/Copenhagen': { country: 'Denmark', city: 'Copenhagen' },
    'Europe/London': { country: 'United Kingdom', city: 'London' },
    'Europe/Paris': { country: 'France', city: 'Paris' },
    'Europe/Berlin': { country: 'Germany', city: 'Berlin' },
    'Europe/Amsterdam': { country: 'Netherlands', city: 'Amsterdam' },
    'Europe/Rome': { country: 'Italy', city: 'Rome' },
    'Europe/Madrid': { country: 'Spain', city: 'Madrid' },
    'Asia/Kolkata': { country: 'India', city: 'Unknown' },
    'Asia/Mumbai': { country: 'India', city: 'Mumbai' },
    'Asia/Tokyo': { country: 'Japan', city: 'Tokyo' },
    'Asia/Shanghai': { country: 'China', city: 'Shanghai' },
    'Asia/Singapore': { country: 'Singapore', city: 'Singapore' },
    'Asia/Dubai': { country: 'UAE', city: 'Dubai' },
    'America/New_York': { country: 'United States', city: 'New York' },
    'America/Los_Angeles': { country: 'United States', city: 'Los Angeles' },
    'America/Chicago': { country: 'United States', city: 'Chicago' },
    'America/Toronto': { country: 'Canada', city: 'Toronto' },
    'America/Vancouver': { country: 'Canada', city: 'Vancouver' },
    'America/Sao_Paulo': { country: 'Brazil', city: 'São Paulo' },
    'Australia/Sydney': { country: 'Australia', city: 'Sydney' },
    'Australia/Melbourne': { country: 'Australia', city: 'Melbourne' },
    'Pacific/Auckland': { country: 'New Zealand', city: 'Auckland' },
  };
  
  return tzMap[tz] || { country: 'Unknown', city: 'Unknown' };
};

const VisitorTracker = () => {
  const sessionStartTime = useRef(Date.now());
  const lastPage = useRef(window.location.pathname);
  const hasTrackedSession = useRef(false);

  // Don't track admin pages
  const isAdminPage = window.location.pathname.startsWith('/admin');

  const trackEvent = async (eventType, additionalData = {}) => {
    // Skip tracking for admin pages
    if (window.location.pathname.startsWith('/admin')) return;
    
    try {
      const location = getApproxLocation();
      
      await axios.post(`${API}/analytics/track`, {
        visitor_id: getVisitorId(),
        event_type: eventType,
        page: window.location.pathname,
        referrer: document.referrer || null,
        source: getSource(),
        country: location.country,
        city: location.city,
        device: getDeviceType(),
        browser: getBrowser(),
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        timestamp: new Date().toISOString(),
        ...additionalData
      });
    } catch (e) {
      // Silently fail - don't affect user experience
      console.debug('Analytics tracking failed:', e);
    }
  };

  useEffect(() => {
    // Track session start only once
    if (!hasTrackedSession.current) {
      hasTrackedSession.current = true;
      trackEvent('session_start');
    }

    // Track page view
    trackEvent('page_view');

    // Track session end on page unload
    const handleUnload = () => {
      const timeSpent = Math.round((Date.now() - sessionStartTime.current) / 1000);
      
      // Use sendBeacon for reliable delivery on page unload
      const data = JSON.stringify({
        visitor_id: getVisitorId(),
        event_type: 'session_end',
        page: window.location.pathname,
        time_on_page: timeSpent,
        timestamp: new Date().toISOString()
      });
      
      navigator.sendBeacon(`${API}/analytics/track`, new Blob([data], { type: 'application/json' }));
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  // Track page changes in SPA
  useEffect(() => {
    const currentPage = window.location.pathname;
    if (lastPage.current !== currentPage) {
      lastPage.current = currentPage;
      trackEvent('page_view');
    }
  });

  return null; // This component doesn't render anything
};

export default VisitorTracker;
